import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, ArrowRight, Loader2, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { HEALTH_QUESTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initScores = () => Object.fromEntries(HEALTH_QUESTIONS.map((q) => [q.key, 60]));

const scoreColor = (v) => (v >= 75 ? "text-volt" : v >= 50 ? "text-slate-900" : "text-destructive");
const barColor = (v) => (v >= 75 ? "bg-volt" : v >= 50 ? "bg-slate-400" : "bg-destructive");

export const HealthCheck = () => {
  const [scores, setScores] = useState(initScores);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [reportId, setReportId] = useState(null);

  const overall = useMemo(() => {
    const vals = Object.values(scores);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [scores]);

  const opportunities = useMemo(
    () =>
      [...HEALTH_QUESTIONS]
        .sort((a, b) => scores[a.key] - scores[b.key])
        .slice(0, 2)
        .map((q) => q.label),
    [scores]
  );

  const submit = async () => {
    if (!email) {
      toast.error("Enter your email to get your score.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/health-check`, { email, company, scores });
      setReportId(res.data.id);
      setDone(true);
      toast.success("Your Warehouse Health Check is ready.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setScores(initScores());
    setEmail("");
    setCompany("");
    setDone(false);
    setReportId(null);
  };

  return (
    <section id="resources" className="border-y border-slate-200 bg-[#F4F6FB]">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
        <Reveal>
          <Kicker num="11">Free Warehouse Health Check</Kicker>
          <SectionHeading className="mt-6">
            Where are your biggest <span className="text-volt">inefficiencies?</span>
          </SectionHeading>
          <p className="mt-6 text-lg leading-relaxed text-slate-500">
            Rate your operation across five areas. We'll score your warehouse health and
            show you exactly where the biggest opportunities are hiding.
          </p>

          <div className="mt-10 space-y-8">
            {HEALTH_QUESTIONS.map((q) => (
              <div key={q.key} data-testid={`health-q-${q.key}`}>
                <div className="mb-3 flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-display font-bold text-slate-900">{q.label}</div>
                    <div className="font-mono-p text-[11px] text-slate-400">{q.q}</div>
                  </div>
                  <span className={`font-display text-2xl font-bold ${scoreColor(scores[q.key])}`}>{scores[q.key]}</span>
                </div>
                <Slider
                  data-testid={`health-slider-${q.key}`}
                  value={[scores[q.key]]}
                  onValueChange={(v) => setScores((s) => ({ ...s, [q.key]: v[0] }))}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="flex items-center">
          <div className="w-full rounded-lg border border-slate-200 bg-white p-8 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.4)]">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div key="capture" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-slate-400">
                    <Activity className="h-4 w-4 text-volt" /> Live score
                  </div>
                  <div className={`mt-3 font-display text-7xl font-black ${scoreColor(overall)}`}>
                    {overall}
                    <span className="text-2xl text-slate-300"> / 100</span>
                  </div>

                  <div className="mt-8 space-y-3">
                    {HEALTH_QUESTIONS.map((q) => (
                      <div key={q.key}>
                        <div className="mb-1 flex justify-between font-mono-p text-[11px]">
                          <span className="text-slate-500">{q.label}</span>
                          <span className="text-slate-700">{scores[q.key]}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100">
                          <div className={`h-full ${barColor(scores[q.key])}`} style={{ width: `${scores[q.key]}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 space-y-3 border-t border-slate-200 pt-6">
                    <Input data-testid="health-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)" className="border-slate-200 bg-white" />
                    <Input data-testid="health-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email *" className="border-slate-200 bg-white" />
                    <button
                      data-testid="health-submit"
                      onClick={submit}
                      disabled={loading}
                      className="group flex w-full items-center justify-center gap-2 bg-volt py-3.5 font-display font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Get my full report <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="health-result">
                  <div className="font-mono-p text-xs uppercase tracking-widest text-slate-400">Overall warehouse health</div>
                  <div className={`mt-2 font-display text-7xl font-black ${scoreColor(overall)}`}>
                    {overall}<span className="text-2xl text-slate-300"> / 100</span>
                  </div>
                  <div className="mt-8 rounded-md border border-volt/40 bg-volt/[0.06] p-5">
                    <div className="font-mono-p text-[11px] uppercase tracking-widest text-volt">Your biggest opportunities</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {opportunities.map((o) => (
                        <span key={o} className="bg-volt px-3 py-1.5 font-display text-sm font-bold text-white">{o}</span>
                      ))}
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">
                      Focus here first — these areas are costing you the most in accuracy,
                      time and space. We've sent a full branded PDF to <span className="text-slate-900">{email}</span>.
                    </p>
                  </div>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    {reportId && (
                      <a
                        data-testid="health-download-pdf"
                        href={`${API}/health-check/${reportId}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-center gap-2 bg-volt px-6 py-3 font-display font-bold text-white transition-transform hover:-translate-y-0.5"
                      >
                        <Download className="h-4 w-4" /> Download PDF report
                      </a>
                    )}
                    <button data-testid="health-reset" onClick={reset} className="inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-900">
                      <RotateCcw className="h-4 w-4" /> Run again
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
